/**
 * Toast Notification Service
 *
 * Provides a simple API for showing toast notifications using Web Awesome's
 * wa-callout component with toast capability.
 *
 * Usage:
 * ```ts
 * import { toast } from '@/services/notifications/toast';
 *
 * toast.success('Data saved!');
 * toast.error('Failed to sync');
 * toast.info('New data available');
 * toast.warning('Connection unstable');
 * ```
 */

export type ToastVariant =
  | "brand"
  | "success"
  | "neutral"
  | "warning"
  | "danger";

export interface ToastOptions {
  /** Duration in ms (0 = no auto-close). Default: 3000 */
  duration?: number;
  /** Icon to show (Web Awesome icon name). Auto-selected by variant if not provided */
  icon?: string;
  /** Whether the toast can be closed by user. Default: true */
  closable?: boolean;
}

const DEFAULT_DURATION = 3000;

const VARIANT_ICONS: Record<ToastVariant, string> = {
  brand: "circle-info",
  success: "circle-check",
  neutral: "gear",
  warning: "triangle-exclamation",
  danger: "octagon-exclamation",
};

/**
 * Escape HTML to prevent XSS when inserting message content.
 */
function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Creates and shows a toast notification.
 */
function showToast(
  message: string,
  variant: ToastVariant,
  options: ToastOptions = {},
): void {
  const { duration = DEFAULT_DURATION, icon, closable = true } = options;

  const iconName = icon ?? VARIANT_ICONS[variant];

  // Create the alert element
  const alert = Object.assign(document.createElement("wa-callout"), {
    variant,
    closable,
    duration,
    innerHTML: `
      <wa-icon name="${iconName}" slot="icon"></wa-icon>
      ${escapeHtml(message)}
    `,
  });

  // Append to body and trigger toast
  document.body.append(alert);

  // Use requestAnimationFrame to ensure element is in DOM before calling toast()
  requestAnimationFrame(() => {
    (alert as any).toast();
  });
}

/**
 * Toast notification API.
 */
export const toast = {
  /**
   * Show a success toast (green).
   */
  success(message: string, options?: ToastOptions): void {
    showToast(message, "success", options);
  },

  /**
   * Show an error toast (red).
   */
  error(message: string, options?: ToastOptions): void {
    showToast(message, "danger", { duration: 5000, ...options });
  },

  /**
   * Show an info toast (blue).
   */
  info(message: string, options?: ToastOptions): void {
    showToast(message, "brand", options);
  },

  /**
   * Show a warning toast (yellow/amber).
   */
  warning(message: string, options?: ToastOptions): void {
    showToast(message, "warning", { duration: 4000, ...options });
  },

  /**
   * Show a neutral toast (gray).
   */
  neutral(message: string, options?: ToastOptions): void {
    showToast(message, "neutral", options);
  },

  /**
   * Show a custom toast with explicit variant.
   */
  show(message: string, variant: ToastVariant, options?: ToastOptions): void {
    showToast(message, variant, options);
  },
};

export default toast;
